"use client";

import { useScrollReveal } from "@/hooks/common/useScrollReveal";
import { clockIcon, computerIcon, customizationIcon } from "@/lib/gcsImagePaths";
import { cn } from "@/lib/utils";
import GcsImage from "./GcsImage";
import {
  clockIconDim,
  computerIconDim,
  customizationIconDim,
} from "@/lib/comparisonData";

interface WhyUsProps {
  title?: string;
  subtitle?: string;
  sectionClassName?: string;
}

export default function WhyUs({
  title = "Why Us",
  subtitle = "We effortlessly find you the right coverage at the best price - and reshop automatically, so you never have to shop for insurance again.",
  sectionClassName,
}: WhyUsProps) {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal();
  const { ref: whyUsItem1Ref, isVisible: whyUsItem1IsVisible } =
    useScrollReveal();
  const { ref: whyUsItem2Ref, isVisible: whyUsItem2IsVisible } =
    useScrollReveal();
  const { ref: whyUsItem3Ref, isVisible: whyUsItem3IsVisible } =
    useScrollReveal();

  return (
    <section
      ref={sectionRef}
      className={cn(
        `py-16 md:py-24 transition-opacity duration-700 will-change-[opacity] ${
          sectionVisible ? "opacity-100" : "opacity-0"
        }`,
        sectionClassName ?? "bg-background",
      )}
      style={{
        visibility: sectionVisible ? "visible" : "hidden",
      }}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="md:max-w-2xl lg:max-w-full md:mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {title}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {subtitle}
            </p>
          </div>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative z-10">
            {/* Choice */}
            <div
              ref={whyUsItem1Ref}
              className={`text-center transition-opacity duration-700 will-change-[opacity] ${
                whyUsItem1IsVisible ? "opacity-100" : "opacity-0"
              }`}
              style={{
                visibility: whyUsItem1IsVisible ? "visible" : "hidden",
              }}
            >
              <div className="flex flex-col items-center h-full">
                <div className="relative w-full max-w-[120px] md:max-w-[100px] mx-auto mb-6 h-24 flex items-start justify-center">
                  <GcsImage
                    path={computerIcon}
                    width={computerIconDim.width}
                    height={computerIconDim.height}
                    alt="Risk IQ personalization tool"
                    className="h-24 md:h-28 w-auto object-contain"
                    priority
                  />
                </div>
                <h3 className="text-xl font-bold mb-3 text-accent uppercase tracking-wide">
                  Choice
                </h3>
                <p className="text-muted-foreground">
                  Our marketplace compares 50+ top carriers to find you the most
                  savings.
                </p>
              </div>
            </div>

            {/* Convenience */}
            <div
              ref={whyUsItem2Ref}
              className={`text-center transition-opacity duration-700 delay-200 will-change-[opacity] ${
                whyUsItem2IsVisible ? "opacity-100" : "opacity-0"
              }`}
              style={{
                visibility: whyUsItem2IsVisible ? "visible" : "hidden",
              }}
            >
              <div className="flex flex-col items-center h-full">
                <div className="relative w-[28%] max-w-xs mx-auto mb-6 h-24 flex items-start justify-center">
                  <GcsImage
                    path={clockIcon}
                    width={clockIconDim.width}
                    height={clockIconDim.height}
                    alt="Insurance carrier marketplace comparison"
                    className="h-20 md:h-24 w-auto object-contain"
                    priority
                  />
                </div>
                <h3 className="text-xl font-bold mb-3 text-accent uppercase tracking-wide">
                  Convenience
                </h3>
                <p className="text-muted-foreground">
                  We automatically reshop your policy - and share the results -
                  so you never overpay again.
                </p>
              </div>
            </div>

            {/* Customization */}
            <div
              ref={whyUsItem3Ref}
              className={`text-center transition-opacity duration-700 delay-400ms will-change-[opacity] ${
                whyUsItem3IsVisible ? "opacity-100" : "opacity-0"
              }`}
              style={{
                visibility: whyUsItem3IsVisible ? "visible" : "hidden",
              }}
            >
              <div className="flex flex-col items-center h-full">
                <div className="relative w-[32%] max-w-xs mx-auto mb-6 h-24 flex items-start justify-center">
                  <GcsImage
                    path={customizationIcon}
                    width={customizationIconDim.width}
                    height={customizationIconDim.height}
                    alt="Customization insurance service"
                    className="h-20 md:h-24 w-auto object-contain"
                    priority
                  />
                </div>
                <h3 className="text-xl font-bold mb-3 text-accent uppercase tracking-wide">
                  Customization
                </h3>
                <p className="text-muted-foreground">
                  Our agents get to know you personally, then craft coverage
                  that fits your home, budget, and lifestyle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
